import React, { useState } from 'react';
import { twoSvApi } from '../../services/api';
import './StepUpDialog.css';

const methodSet = (challenge) => new Set((challenge?.methods || []).map((method) => String(method).toLowerCase()));

const StepUpDialog = ({
  challenge,
  title = 'Two-step verification required',
  description = 'This page needs a fresh high-security verification before it can load protected network data.',
  onComplete,
  onCancel,
}) => {
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [working, setWorking] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  if (!challenge) {
    return null;
  }

  const methods = methodSet(challenge);
  const hasPush = methods.has('push');
  const hasTotp = methods.has('totp');
  const hasBackupCode = methods.has('backup_code') || methods.has('backup-code') || methods.has('backup');
  const hasPushDevice = Boolean(challenge.pushDeviceId);

  const getApiMessage = (err, fallback) => (
    err.response?.data?.message
    || err.response?.data?.error?.message
    || err.message
    || fallback
  );

  const finish = async () => {
    setMessage('');
    setError('');
    await onComplete?.();
  };

  const submitTotp = async (event) => {
    event.preventDefault();
    if (!totpCode.trim()) {
      setError('Enter a verification code from your authenticator app.');
      return;
    }

    try {
      setWorking('totp');
      setError('');
      await twoSvApi.satisfyChallengeTotp(challenge.id, totpCode.trim());
      await finish();
    } catch (err) {
      setError(getApiMessage(err, 'The verification code was not accepted.'));
    } finally {
      setWorking('');
    }
  };

  const submitBackupCode = async (event) => {
    event.preventDefault();
    if (!backupCode.trim()) {
      setError('Enter a backup code.');
      return;
    }

    try {
      setWorking('backup');
      setError('');
      await twoSvApi.satisfyChallengeBackupCode(challenge.id, backupCode.trim());
      await finish();
    } catch (err) {
      setError(getApiMessage(err, 'The backup code was not accepted.'));
    } finally {
      setWorking('');
    }
  };

  const sendPush = async () => {
    try {
      setWorking('push');
      setError('');
      setMessage('');
      await twoSvApi.sendChallengePush(challenge.id);
      setMessage('Verification sent. Approve it on your phone, then retry the protected request.');
    } catch (err) {
      setError(getApiMessage(err, 'Unable to send verification to your phone.'));
    } finally {
      setWorking('');
    }
  };

  return (
    <div className="step-up-overlay" role="presentation">
      <section
        className="step-up-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="step-up-title"
        data-testid="step-up-dialog"
      >
        <div className="step-up-header">
          <div>
            <h2 id="step-up-title">{title}</h2>
            <p>{description}</p>
          </div>
          {onCancel ? (
            <button type="button" className="step-up-close" onClick={onCancel} aria-label="Close verification dialog">
              x
            </button>
          ) : null}
        </div>

        <div className="step-up-detail">
          <span>Required assurance</span>
          <strong>{challenge.requiredSal ? `SAL ${challenge.requiredSal}` : 'Fresh verification'}</strong>
          {challenge.expiresAt ? <small>Challenge expires {new Date(challenge.expiresAt).toLocaleTimeString('en-GB')}</small> : null}
        </div>

        {hasPush ? (
          <div className="step-up-method">
            <h3>Phone approval</h3>
            {hasPushDevice ? (
              <button type="button" className="network-primary-button" onClick={sendPush} disabled={Boolean(working)}>
                {working === 'push' ? 'Sending...' : 'Send verification to your phone'}
              </button>
            ) : (
              <p data-testid="step-up-no-device">No device is enabled for 2FA.</p>
            )}
          </div>
        ) : null}

        {hasTotp ? (
          <form className="step-up-method" onSubmit={submitTotp}>
            <label htmlFor="step-up-totp-code">Authenticator code</label>
            <div className="step-up-inline">
              <input
                id="step-up-totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(event) => setTotpCode(event.target.value)}
                placeholder="123456"
              />
              <button type="submit" className="network-primary-button" disabled={Boolean(working)}>
                {working === 'totp' ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>
        ) : null}

        {hasBackupCode ? (
          <form className="step-up-method" onSubmit={submitBackupCode}>
            <label htmlFor="step-up-backup-code">Backup code</label>
            <div className="step-up-inline">
              <input
                id="step-up-backup-code"
                type="text"
                autoComplete="one-time-code"
                value={backupCode}
                onChange={(event) => setBackupCode(event.target.value)}
                placeholder="ABCD-EFGH"
              />
              <button type="submit" className="network-secondary-button" disabled={Boolean(working)}>
                {working === 'backup' ? 'Checking...' : 'Use backup code'}
              </button>
            </div>
          </form>
        ) : null}

        {!hasPush && !hasTotp && !hasBackupCode ? (
          <p>No supported 2FA method was returned for this challenge.</p>
        ) : null}

        {message ? (
          <div className="step-up-message" data-testid="step-up-message">
            {message}
            <button type="button" className="network-secondary-button" onClick={finish} disabled={Boolean(working)}>
              Retry protected request
            </button>
          </div>
        ) : null}
        {error ? <div className="step-up-error" data-testid="step-up-error">{error}</div> : null}
      </section>
    </div>
  );
};

export default StepUpDialog;
